import {
  IController,
  resource,
  IQService,
  ILocationService,
  IRootScopeService,
} from "angular";
import { difference, uniq } from "lodash";
import { iCategory, iCategoryType } from "@/interfaces/categories";

(function (angular) {
  "use strict";

  class Controller implements IController {
    /**
     * The categorizable type binding.
     */
    public readonly categorizable: string;

    /**
     * The currently expanded category type.
     */
    public expandedTypeId: number | null = null;

    /**
     * The onCategoryChange binding.
     */
    public readonly onCategoryChange: (selected: string[]) => void;

    /**
     * The parameter binding.
     */
    public readonly parameter: string;

    /**
     * The selected categories binding.
     */
    public readonly selected: string[];

    /**
     * The filters title binding.
     */
    public readonly title: string;

    /**
     * The loaded category types.
     */
    public types: iCategoryType[] = [];

    /**
     * The $location service.
     */
    protected $location: ILocationService;

    /**
     * The $q service.
     */
    protected $q: IQService;

    /**
     * The $rootScope service.
     */
    protected $rootScope: IRootScopeService;

    /**
     * The categories API.
     */
    protected categoriesApi: resource.IResourceClass<iCategory>;

    /**
     * The category types API.
     */
    protected categoryTypesApi: resource.IResourceClass<iCategoryType>;

    /**
     * The tree manager.
     */
    protected treeManager: any;

    /**
     * @param $location
     * @param $q
     * @param $scope
     * @param categoriesApi
     * @param categoryTypesApi
     * @param treeManager
     */
    public constructor(
      $location: ILocationService,
      $q: IQService,
      $rootScope: IRootScopeService,
      categoriesApi: resource.IResourceClass<iCategory>,
      categoryTypesApi: resource.IResourceClass<iCategoryType>,
      treeManager: any,
    ) {
      this.$location = $location;
      this.$q = $q;
      this.$rootScope = $rootScope;
      this.categoriesApi = categoriesApi;
      this.categoryTypesApi = categoryTypesApi;
      this.treeManager = treeManager;

      $rootScope.$on(
        "$locationChangeSuccess",
        this.onLocationChangeSuccess.bind(this),
      );
    }

    /**
     * Called after the components controller has initialized and all bindings are available.
     */
    public $onInit(): void {
      this.loadCategories();
    }

    /**
     * Returns whether the given category is selected.
     * @param category
     */
    public isCategorySelected(category: iCategory): boolean {
      // Stops undefined error if no categories have been selected
      if (typeof this.selected === "undefined") {
        return false;
      }

      return this.selected.includes(category.slug);
    }

    /**
     * Returns whether the given type is expanded.
     * @param type
     */
    public isTypeExpanded(type: iCategoryType): boolean {
      return this.expandedTypeId === type.id;
    }

    /**
     * Toggles the expanded category type.
     * @param type
     */
    public toggleExpandedType(type: iCategoryType): void {
      const id = type.id;

      if (this.expandedTypeId === id) {
        this.expandedTypeId = null;
      } else {
        this.expandedTypeId = id;
      }
    }

    /**
     * Toggles the given category.
     * @param category
     */
    public toggleSelectedCategory(category: iCategory): void {
      const value = category.slug;

      if (this.selected.includes(value)) {
        this.onCategoryChange(this.deselectCategory(category));
      } else {
        this.onCategoryChange(this.selectCategory(category));
      }
    }

    /**
     * Deselects a category (including it's parents and children.)
     * @param toRemove
     */
    protected deselectCategory(toRemove: iCategory): string[] {
      const categories = [
        ...this.getParentCategories(toRemove),
        ...this.getChildCategories(toRemove),
      ];

      const selected = difference(
        this.selected,
        [toRemove.slug],
        categories.map((category) => category.slug),
      );

      this.setQueryParams(selected);

      return selected;
    }

    /**
     * Returns all decendents of the given category.
     * @param parent
     */
    protected getChildCategories(parent: iCategory): iCategory[] {
      const type = this.types.find((type) => type.id === parent.type_id);

      if (!type || !type.categories) {
        return [];
      }

      let parents = [parent.id];

      // We reduce, rather than reduce right, because category IDs are decending (because they're a tree.)
      return type.categories.reduce((categories, category) => {
        if (parents.includes(category.parent_id)) {
          categories.push(category);
          parents.push(category.id);
        }

        return categories;
      }, []);
    }

    /**
     * Returns all ancestors of the given category.
     * @param child
     */
    protected getParentCategories(child: iCategory): iCategory[] {
      const type = this.types.find((type) => type.id === child.type_id);

      if (!child.parent_id || !type || !type.categories) {
        return [];
      }

      let parents = [child.parent_id];

      // We reduce right because category IDs are decending (because they're a tree.)
      return type.categories.reduceRight((categories, category) => {
        if (parents.includes(category.id)) {
          categories.push(category);

          if (!!category.parent_id) {
            parents.push(category.parent_id);
          }
        }

        return categories;
      }, []);
    }

    /**
     * Returns the parent of the given category.
     * @param child
     */
    protected getParentCategory(child: iCategory): iCategory | null {
      const type = this.types.find((type) => type.id === child.type_id);

      if (!child.parent_id || !type || !type.categories) {
        return null;
      }

      return (
        type.categories.find((category) => category.id === child.parent_id) ||
        null
      );
    }

    /**
     * Loads category types with categories.
     */
    protected loadCategories(): void {
      this.$q
        .all([
          this.categoryTypesApi.query({
            "categorizables[]": [this.categorizable],
          }).$promise,
          this.categoriesApi.query().$promise,
        ])
        .then((promises) => {
          this.types = promises[0].map((type) => {
            type.categories = this.treeManager.buildTree(promises[1], type);
            return type;
          });
        });
    }

    /**
     * Selects the given category's parent, if all if its siblings are active. If
     * parent is selected, recursively calls itself with parent.
     * @param toAdd
     * @param selected
     */
    protected maybeSelectParent(
      toAdd: iCategory,
      selected: string[],
    ): string[] {
      if (!toAdd.parent_id) {
        return selected;
      }

      const parent = this.getParentCategory(toAdd);
      const selectParent = this.getChildCategories(parent).reduce(
        (select, category) => {
          return select && selected.includes(category.slug);
        },
        true,
      );

      if (!selectParent) {
        return selected;
      }

      selected.push(parent.slug);

      return this.maybeSelectParent(parent, selected);
    }

    /**
     * Watches for parameter changes so we can propogate state (makes the browser back/forward
     * button work.)
     */
    protected onLocationChangeSuccess(): void {
      const selected = [].concat(this.$location.search()[this.parameter] || []);
      const changes = [
        ...difference(this.selected, selected),
        ...difference(selected, this.selected),
      ];

      if (changes.length > 0) {
        this.onCategoryChange(selected);
      }
    }

    /**
     * Selects a category and its children (and parents, if appropriate.)
     * @param toAdd
     */
    protected selectCategory(toAdd: iCategory): string[] {
      const selected = this.maybeSelectParent(
        toAdd,
        uniq([
          ...this.selected,
          toAdd.slug,
          ...this.getChildCategories(toAdd).map((category) => category.slug),
        ]),
      );

      this.setQueryParams(selected);

      return selected;
    }

    /**
     * Sets the categories parameter on the URL.
     * @param selected
     */
    protected setQueryParams(selected: string[]): void {
      this.$location.search(this.parameter, selected);
    }
  }

  // Inject some toys.
  Controller.$inject = [
    "$location",
    "$q",
    "$scope",
    "CategoriesApi",
    "CategoryTypesApi",
    "TreeManager",
  ];

  // Register the component.
  angular.module("app").component("categoryFilters", {
    bindings: {
      categorizable: "@",
      onCategoryChange: "<",
      parameter: "@",
      selected: "<",
      title: "@",
    },
    controller: Controller,
    templateUrl: "components/categoryFilters.html",
  });
})(angular);
